import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumns } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class DWithin extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.stdwithin',
        group: 'Spatial Operations',
        title: 'ST DWithin',
        description: `
      This component calculates if geographies, of an input table
      and those of a second table that match a given criteria to join them, are withing a given distance .
      `,
        starred: true
      },
      [
        {
          name: 'main',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'secondary',
          type: WorkflowComponentParamType.InputTable,
          title: 'Secondary table'
        },
        {
          name: 'geomain',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in main table',
          parent: 'main',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geosecondary',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in secondary table',
          parent: 'secondary',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'joinmain',
          type: WorkflowComponentParamType.Column,
          title: 'Column in main table for joining',
          parent: 'main'
        },
        {
          name: 'joinsecondary',
          type: WorkflowComponentParamType.Column,
          title: 'Column in secondary table for joining',
          parent: 'secondary'
        },
        {
          name: 'maxdistance',
          type: WorkflowComponentParamType.Number,
          title: 'Max Distance'
        }
      ]
    )
  }

  getInnerSql({ params, connection, envVars }: WorkflowComponentCodeParams) {
    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake:
        return dedent`
          SELECT
            a.*,
            b.${params.geosecondary} AS ${params.geosecondary}_joined,
            ST_DWITHIN(
              ${connection.processInputGeogColumn(`a.${params.geomain}`)},
              ${connection.processInputGeogColumn(`b.${params.geosecondary}`)},
              ${params.maxdistance}
            ) AS dwithin
          FROM
            ${connection.quoteName(params.main as string)} a,
            ${connection.quoteName(params.secondary as string)} b
          WHERE a.${params.joinmain} = b.${params.joinsecondary}`
      case Provider.Postgres:
        return dedent`
          SELECT
            a.*,
            b.${params.geosecondary} AS ${params.geosecondary}_joined,
            ST_DWITHIN(
              ${connection.processInputGeogColumn(
                `a.${params.geomain}`
              )}::geography,
              ${connection.processInputGeogColumn(
                `b.${params.geosecondary}`
              )}::geography,
              ${params.maxdistance}
            ) AS dwithin
          FROM
            ${connection.quoteName(params.main as string)} a,
            ${connection.quoteName(params.secondary as string)} b
          WHERE a.${params.joinmain} = b.${params.joinsecondary}`
      case Provider.Redshift:
        return dedent`
          SELECT
            a.*,
            b.${params.geosecondary} AS ${params.geosecondary}_joined,
            ST_DWITHIN(
              ST_TRANSFORM(a.${params.geomain}, 3857),
              ST_TRANSFORM(b.${params.geosecondary}, 3857),
              ${params.maxdistance}
            ) AS dwithin
          FROM
            ${connection.quoteName(params.main as string)} a,
            ${connection.quoteName(params.secondary as string)} b
          WHERE a.${params.joinmain} = b.${params.joinsecondary}`
      case Provider.DatabricksRest:
        return dedent`
            SELECT  /*+ REPARTITION(40) */
              a.*,
              b.${params.geosecondary} AS ${params.geosecondary}_joined,
              SEDONA_ST_DWITHIN(
                SEDONA_ST_TRANSFORM(SEDONA_ST_SETSRID(SEDONA_ST_GEOMFROMWKB(a.${
                  params.geomain
                }), 4326), 'epsg:3857'),
                SEDONA_ST_TRANSFORM(SEDONA_ST_SETSRID(SEDONA_ST_GEOMFROMWKB(b.${
                  params.geosecondary
                }), 4326), 'epsg:3857'),
                ${params.maxdistance}
              ) AS dwithin
            FROM
              ${connection.quoteName(params.main as string)} a,
              ${connection.quoteName(params.secondary as string)} b
            WHERE a.${params.joinmain} = b.${params.joinsecondary}`
      default:
        return null
    }
  }

  getOptimizationColumn(params: Record<string, unknown>) {
    return params.geomain as string
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    return addColumns(columns.main, [
      { name: 'dwithin', type: ColumnDataType.Boolean },
      {
        name: `${params.geosecondary}_joined`,
        type: connection.getGeoDataType()
      }
    ])
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest
    ]
  }
}

export { DWithin }
